import omit from 'lodash/omit';
import pick from 'lodash/pick';
import {useState} from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from "@mui/material/Grid";
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import FormInput from '../common/FormInput';
import useUser from '../../hooks/useUser';
import {useCreateLeadMutation, useUpdateLeadMutation} from '../../redux/services/leadApi';
import {useGetDiagnosisCategoriesQuery} from '../../redux/services/diagnosisCategoryApi';
import { 
  getLeadStatusOptions,
  getMarketingStrategyOptions,
  getReferralSourceCategories,
  getStates
} from '../../helpers/selectOptions';

const sections = [
  {value: 'metadata', label: 'Summary'},
  {value: 'contact', label: 'Contact'},
  {value: 'location', label: 'Location'},
  {value: 'referral', label: 'Referral'},
  {value: 'details', label: 'Details'},
];

const getFields = ({provider, diagnosisCategories, editing}) => [
  {
    name: 'status',
    section: 'metadata',
    label: 'Status',
    type: 'select',
    validate: () => true,
    fullWidth: true,
    required: true,
    disabled: !editing,
    editableOnCreate: false,
    options: (
      editing ? [
        {value: 'ACTIVE', label: 'Active'},
        {value: 'LOST', label: 'Lost'},
        {value: 'ARCHIVED', label: 'Archived'},
      ]
      : [{value: 'NEW', label: 'New'}]
    ),
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'provider_id',
    section: 'metadata',
    label: 'Provider',
    type: 'selectProvider',
    editableAfterCreate: true,
    fullWidth: true,
    helperText: null,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
    },
    {
      name: 'diagnosis_category_id',
      section: 'metadata',
      label: 'Diagnosis Category',
      type: 'select',
      editableAfterCreate: true,
      required: false,
      fullWidth: true,
      validate: () => true,
      options: (diagnosisCategories || []).map(
        c => ({value: c.id, label: c.name})
      ),
      grid: {
        xs: 12,
        md: 6,
      },
    },
    {
      name: 'facility_id',
      section: 'metadata',
      label: 'Team',
      type: 'selectLocation',
      editableAfterCreate: true,
      required: false,
      fullWidth: true,
      validate: () => true,
      grid: {
        xs: 12,
        md: 6,
      },
    },
    {
    name: 'lost_category',
    section: 'metadata',
    label: 'Reason Lost (Category)',
    type: 'select',
    editableAfterCreate: true,
    required: true,
    fullWidth: true,
    formDataFilter: formData => formData.status === 'LOST',
    validate: () => true,
    options: [
      {value: 'COST', label: 'Insurance or Cost Objection'},
      {value: 'GHOSTED', label: 'Ghosted! (No Response)'},
      {value: 'TIMING', label: 'Timing Not Right'},
      {value: 'DISTANCE', label: 'Outside of Treatment Radius'},
      {value: 'RESOLVED', label: 'Pain Resolved'},
      {value: 'REFERRED_OUT', label: 'Referred Out'},
      {value: 'OTHER', label: 'Other Reason (please note in comment)'},
    ],
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'why_lost',
    section: 'metadata',
    label: 'Reason Lost (Comment)',
    type: 'textarea',
    rows: 3,
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    formDataFilter: formData => formData.status === 'LOST',
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  // {
  //   name: 'patient_id',
  //   section: 'metadata',
  //   label: 'Linked Patient',
  //   type: 'selectPatient',
  //   editableAfterCreate: true,
  //   disabled: true,
  //   required: true,
  //   fullWidth: true,
  //   helperText: 'Link lead to the patient once they have registered',
  //   validate: () => true,
  //   grid: {
  //     xs: 12,
  //     md: 6,
  //   },
  // },
  {
    name: 'contact_first_name',
    section: 'contact',
    label: 'First Name',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'contact_last_name',
    section: 'contact',
    label: 'Last Name',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'contact_email',
    section: 'contact',
    label: 'Email',
    type: 'email',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  // {
  //   name: 'enrolled_in_emails',
  //   label: 'Enroll in Automatic Emails to Encourage Signing Up?',
  //   type: 'boolean',
  //   editableAfterCreate: true,
  //   required: false,
  //   fullWidth: true,
  //   validate: () => true,
  //   grid: {
  //     xs: 12,
  //     md: 6,
  //   },
  // },
  {
    name: 'contact_phone',
    section: 'contact',
    label: 'Phone',
    type: 'phone',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  // {
  //   name: 'address',
  //   label: 'Address',
  //   type: 'address',
  //   editableAfterCreate: true,
  //   required: false,
  //   fullWidth: true,
  //   validate: () => true,
  //   grid: {
  //     xs: 12,
  //     md: 6,
  //   },
  // },
  {
    name: 'contact_address_line_1',
    section: 'location',
    label: 'Address Line 1',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'contact_address_line_2',
    section: 'location',
    label: 'Address Line 2',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'contact_city',
    section: 'location',
    label: 'City',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'contact_state',
    section: 'location',
    label: 'State',
    type: 'select',
    editableAfterCreate: true,
    required: true,
    fullWidth: true,
    validate: () => true,
    options: getStates(),
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'contact_zip',
    section: 'location',
    label: 'Zip Code',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'referral_category',
    section: 'referral',
    label: 'Referral Category',
    type: 'select',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    options: getReferralSourceCategories().map(c => ({value: c.value.toUpperCase(), label: c.label})),
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'referral_details',
    section: 'referral',
    label: 'Referrer Name',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'referrer_company',
    section: 'referral',
    label: 'Referrer Company',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  // {
  //   name: 'marketing_strategies',
  //   label: 'Marketing Strategies',
  //   type: 'select',
  //   editableAfterCreate: true,
  //   required: false,
  //   fullWidth: true,
  //   validate: () => true,
  //   options: getMarketingStrategyOptions(),
  //   grid: {
  //     xs: 12,
  //     md: 6,
  //   },
  // },
  // {
  //   name: 'why_lost',
  //   section: 'details',
  //   label: 'If lost lead, why?',
  //   type: 'textarea',
  //   rows: 3,
  //   fullWidth: true,
  //   editableAfterCreate: true,
  //   required: false,
  //   validate: () => true,
  //   grid: {
  //     xs: 12,
  //     md: 6,
  //   },
  // },
  {
    name: 'note',
    section: 'details',
    label: 'Notes',
    editableAfterCreate: true, 
    required: false,
    type: 'textarea',
    rows: 5,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 12,
    },
  },
];

const handleFieldUpdate = ({formValues, setFormValues}) => name => e => {
  setFormValues({...formValues, [name]: e?.target?.value});
};

const parseFormValues = formValues => {
  let cleanData = omit(formValues, 'address');
  if (formValues.address) {
    cleanData = {
      ...cleanData,
      contact_address_line_1: formValues.address.line1,
      contact_address_line_2: formValues.address.line2,
      contact_city: formValues.address.city?.value,
      contact_state: formValues.address.state?.value,
      contact_zip: formValues.address.zip,
    };
  }
  return cleanData;
};

const handleSubmit = ({
  id, 
  editing, 
  sendRequest, 
  afterSubmit,
  setSubmitting,
  setError,
}) => async (e, formValues) => {
  setSubmitting(true);
  const body = parseFormValues(formValues);
  const params = editing ? {id, body} : body;
  const response = await sendRequest(params);
  setSubmitting(false);
  if (response.error) {
    // TODO: handle error
    return;
  }
  if (response.data) {
    console.log('Lead saved successfully:', response.data);
    if (afterSubmit) afterSubmit();
  }
};

const getDefaultFormValues = ({fields, lead, editing, user, isAdmin}) => (
  editing 
  ? {
    ...pick(lead, 
      ...fields.filter(f => f.editableAfterCreate).map(f => f.name)
    ),
    referral_category: (lead?.referral_category || '').toUpperCase(),
    status: ['NEW', 'CREATED_ACCOUNT', 'COMPLETED_SIGNUP', 'SCHEDULED'].includes(lead.status) 
      ? 'ACTIVE'
      : lead.status,
    provider_id: lead?.provider_id || lead?.provider?.id,
    facility_id: lead?.provider_id ? undefined : lead?.facility_id,
  } 
  : {
    status: 'NEW', 
    provider_id: isAdmin ? null : user?.provider?.id,
    contact_state: user?.provider?.state
  } 
);

const LeadForm = props => {
  const {lead: l, editing, user, diagnosisCategories, isAdmin} = props;
  const fields = getFields({
    provider: user?.provider, 
    editing,
    diagnosisCategories,
  });
  const [submitting, setSubmitting] = useState(false);
  console.log('LeadForm props:', props);
  const defaultFormValues = getDefaultFormValues({fields, lead: l, editing, user, isAdmin});
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [putLead] = useUpdateLeadMutation();
  const [postLead] = useCreateLeadMutation();
  const onSubmit = handleSubmit({
    id: editing ? l.id : undefined,
    editing,
    sendRequest: editing ? putLead : postLead,
    afterSubmit: props.afterSubmit,
    setSubmitting,
  });
  return (
    <Box sx={{width: '100%'}}>
      <Stack spacing={0}>
        <Typography variant='h2' align='center' paddingTop={0} paddingBottom={0}>
          Lead Details
        </Typography>
        <Grid container spacing={2} direction={{xs: 'row'}} padding={2} paddingTop={0}>
          {sections.map(s =>
            <>
              <Grid item xs={12} key={s.value} marginTop={4}>
                <Typography variant='h3' gutterBottom>
                  {s.label}
                </Typography>
                <Divider />
              </Grid>
              <>
                {fields
                  .filter(f => 
                    f.section === s.value 
                    && (f.formDataFilter ? f.formDataFilter(formValues) : true)
                  )
                  .filter(f => 
                    f.name === 'facility_id' 
                    ? !formValues.provider_id
                    : true
                  )
                  .map(f =>
                    <Grid item xs={f.grid?.xs ?? 12} sm={f.grid?.sm} md={f.grid?.md}>
                      <FormInput
                        defaultValue={defaultFormValues?.[f.name] || f.defaultValue}
                        onChange={handleFieldUpdate({formValues, setFormValues})(f.name)}
                        formValues={formValues}
                        {...f}
                      />
                  </Grid>
                )}
              </>
            </>
          )}
          <Grid item xs={12}>
            <Stack alignItems='center'>
              <Box sx={{width: 'fit-content'}}>
                <Button
                  variant='primaryGradient'
                  disabled={submitting}
                  onClick={e => onSubmit(e, formValues)}
                >
                  {editing ? 'Save Changes' : 'Save New Lead'}
                </Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

const LeadFormContainer = props => {
  const user = useUser();
  const diagnosisCategoriesQuery = useGetDiagnosisCategoriesQuery();
  const diagnosisCategories = diagnosisCategoriesQuery?.data?.data || [];
  if (!user || !diagnosisCategories || !diagnosisCategories.length) return <CircularProgress size={20} />
  return <LeadForm {...props} user={user} diagnosisCategories={diagnosisCategories} />
};

LeadFormContainer.propTypes = {
  editable: PropTypes.bool,
  lead: PropTypes.object,
};

export default LeadFormContainer;